import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  ICardListGroup,
  ICardListGroupConfig,
  ICardListItem,
  ICardListState,
} from '../../components/CardList/types'
import { compareGroupsByLabel } from '../../utils/compareGroupsByLabel'
import useGroupExpansion from '../useGroupExpansion'

interface UseCardListCoreOptions<T extends ICardListItem> {
  items: T[]
  groupConfig?: ICardListGroupConfig<T>
  /** Collapse every band on first sight, or only the bands the predicate picks. */
  defaultCollapsed?: boolean | ((groupKey: string) => boolean)
  /** Controlled mode: hook delegates to caller's Set; defaultCollapsed is ignored. */
  collapsedKeys?: Set<string>
  onCollapsedKeysChange?: (keys: Set<string>) => void
}

export function useCardListCore<T extends ICardListItem>(
  options: UseCardListCoreOptions<T>,
): ICardListState<T> {
  const { items, groupConfig, defaultCollapsed = false } = options
  const getGroupAncestors = groupConfig?.getGroupAncestors
  const isControlled = options.collapsedKeys !== undefined

  const groupKeys = useMemo(() => {
    if (!groupConfig) {
      return []
    }

    const keys = new Set<string>()
    items.forEach(item => {
      const keyOrKeys = groupConfig.groupBy(item)
      const keyArr = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys]
      keyArr.forEach(key => {
        keys.add(key)
        // Tree mode: intermediate ancestor buckets emit a header even with no direct item.
        if (getGroupAncestors) {
          for (const ancestor of getGroupAncestors(key)) keys.add(ancestor)
        }
      })
    })

    return Array.from(keys)
  }, [items, groupConfig, getGroupAncestors])

  const shouldCollapseRef = useRef(defaultCollapsed)
  shouldCollapseRef.current = defaultCollapsed
  const shouldCollapse = useCallback((key: string) => {
    const rule = shouldCollapseRef.current
    return typeof rule === 'function' ? rule(key) : !!rule
  }, [])

  // Keys the default has already been applied to. The default runs once per key, so a band
  // the user expanded stays expanded when groupKeys changes (a search, a regroup).
  const seenKeysRef = useRef<Set<string> | null>(null)
  if (!seenKeysRef.current) seenKeysRef.current = new Set(groupKeys)

  const [internalCollapsedKeys, setInternalCollapsedKeys] = useState<
    Set<string>
  >(() => new Set(groupKeys.filter(shouldCollapse)))

  // Reconcile when groupKeys grows after mount (async-loaded items, a regroup).
  useEffect(() => {
    if (isControlled || !defaultCollapsed) return
    const seen = seenKeysRef.current
    const unseen = groupKeys.filter(key => !seen.has(key))
    if (unseen.length === 0) return
    unseen.forEach(key => seen.add(key))
    const toCollapse = unseen.filter(shouldCollapse)
    if (toCollapse.length === 0) return
    setInternalCollapsedKeys(prev => {
      const next = new Set(prev)
      toCollapse.forEach(key => next.add(key))
      return next
    })
  }, [isControlled, defaultCollapsed, groupKeys, shouldCollapse])

  // Inert path: controlled mode without onCollapsedKeysChange (snapshot/preview surfaces).
  // Dev-only one-shot warning so a forgotten handler doesn't masquerade as frozen UI.
  const noopWarnedRef = useRef(false)
  const noop = useCallback((): void => {
    if (process.env.NODE_ENV !== 'production' && !noopWarnedRef.current) {
      noopWarnedRef.current = true
      // eslint-disable-next-line no-console
      console.warn(
        '[useCardListCore] Controlled mode (`collapsedKeys` provided) ' +
          'without `onCollapsedKeysChange` — toggle / collapse / expand ' +
          'actions will silently no-op. Pass an explicit no-op handler ' +
          'to suppress this warning if the snapshot/preview behavior is ' +
          'intentional.',
      )
    }
  }, [])

  const collapsedKeys = options.collapsedKeys ?? internalCollapsedKeys
  const onCollapsedKeysChange = isControlled
    ? (options.onCollapsedKeysChange ?? noop)
    : setInternalCollapsedKeys

  const expansion = useGroupExpansion({
    groupKeys,
    collapsedKeys,
    onCollapsedKeysChange,
    getGroupAncestors,
  })

  const groups = useMemo((): ICardListGroup<T>[] => {
    if (!groupConfig) {
      return []
    }

    const groupMap = new Map<string, T[]>()
    // Tree mode: count = direct items + items in any descendant bucket.
    const descendantCount: Map<string, number> | null = getGroupAncestors
      ? new Map()
      : null

    items.forEach(item => {
      const keyOrKeys = groupConfig.groupBy(item)
      const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys]
      // Per-item ancestor dedup: a multi-bucket item with a common
      // ancestor must not roll up twice into that ancestor's count.
      const countedAncestors = new Set<string>()
      keys.forEach(key => {
        const existing = groupMap.get(key)
        if (existing) {
          existing.push(item)
        } else {
          groupMap.set(key, [item])
        }
        if (getGroupAncestors && descendantCount) {
          descendantCount.set(key, (descendantCount.get(key) ?? 0) + 1)
          for (const ancestor of getGroupAncestors(key)) {
            if (!groupMap.has(ancestor)) groupMap.set(ancestor, [])
            if (countedAncestors.has(ancestor)) continue
            countedAncestors.add(ancestor)
            descendantCount.set(
              ancestor,
              (descendantCount.get(ancestor) ?? 0) + 1,
            )
          }
        }
      })
    })

    let sortedKeys = Array.from(groupMap.keys())

    if (groupConfig.sortGroups) {
      sortedKeys = sortedKeys.sort(groupConfig.sortGroups)
    } else {
      const labelByKey = new Map(
        sortedKeys.map(key => [
          key,
          groupConfig.getGroupLabel(
            key,
            descendantCount
              ? (descendantCount.get(key) ?? 0)
              : (groupMap.get(key)?.length ?? 0),
          ),
        ]),
      )
      sortedKeys = sortedKeys.sort((a, b) =>
        compareGroupsByLabel(a, b, key => labelByKey.get(key) ?? ''),
      )
    }

    return sortedKeys.map(key => {
      let groupItems = groupMap.get(key) || []

      if (groupConfig.sortItems) {
        groupItems = [...groupItems].sort(groupConfig.sortItems)
      }

      const depth = getGroupAncestors ? getGroupAncestors(key).length : 0
      const count = descendantCount
        ? (descendantCount.get(key) ?? 0)
        : groupItems.length

      return {
        key,
        label: groupConfig.getGroupLabel(key, count),
        count,
        items: groupItems,
        isCollapsed: expansion.isCollapsed(key),
        depth,
      }
    })
    // Depend on stable expansion.isCollapsed; expansion object is rebuilt each render.
  }, [items, groupConfig, expansion.isCollapsed, getGroupAncestors])

  const flatItems = useMemo(() => {
    if (!groupConfig) {
      return items
    }

    const result: T[] = []
    groups.forEach(group => {
      if (!group.isCollapsed) {
        result.push(...group.items)
      }
    })
    return result
  }, [items, groupConfig, groups])

  // Visible = no ancestor in collapsedKeys; drives the incremental controls.
  const visibleGroups = useMemo(() => {
    if (!getGroupAncestors) return groups
    return groups.filter(
      g => !getGroupAncestors(g.key).some(a => collapsedKeys.has(a)),
    )
  }, [groups, getGroupAncestors, collapsedKeys])

  const canExpandMore = useMemo(
    () => visibleGroups.some(g => collapsedKeys.has(g.key)),
    [visibleGroups, collapsedKeys],
  )

  const canCollapseMore = useMemo(
    () => visibleGroups.some(g => !collapsedKeys.has(g.key)),
    [visibleGroups, collapsedKeys],
  )

  const collapseAllGroups = useCallback(() => {
    if (groups.length === 0) return
    let mutated = false
    const next = new Set(collapsedKeys)
    for (const g of groups) {
      if (!next.has(g.key)) {
        next.add(g.key)
        mutated = true
      }
    }
    if (mutated) onCollapsedKeysChange(next)
  }, [groups, collapsedKeys, onCollapsedKeysChange])

  const expandAllGroups = useCallback(() => {
    if (groups.length === 0) return
    let mutated = false
    const next = new Set(collapsedKeys)
    for (const g of groups) {
      if (next.delete(g.key)) mutated = true
    }
    if (mutated) onCollapsedKeysChange(next)
  }, [groups, collapsedKeys, onCollapsedKeysChange])

  const incrementalExpand = useCallback(() => {
    // Uncollapse all bands at the deepest visible-collapsed depth; one press = one layer.
    const candidates = visibleGroups.filter(g => collapsedKeys.has(g.key))
    if (candidates.length === 0) return
    const maxDepth = candidates.reduce((acc, g) => Math.max(acc, g.depth), 0)
    const next = new Set(collapsedKeys)
    let mutated = false
    for (const g of candidates) {
      if (g.depth === maxDepth && next.delete(g.key)) mutated = true
    }
    if (mutated) onCollapsedKeysChange(next)
  }, [visibleGroups, collapsedKeys, onCollapsedKeysChange])

  const incrementalCollapse = useCallback(() => {
    // Symmetric inverse: collapse leaf bands too (they own items even without children).
    const candidates = visibleGroups.filter(g => !collapsedKeys.has(g.key))
    if (candidates.length === 0) return
    const maxDepth = candidates.reduce((acc, g) => Math.max(acc, g.depth), 0)
    const next = new Set(collapsedKeys)
    let mutated = false
    for (const g of candidates) {
      if (g.depth === maxDepth && !next.has(g.key)) {
        next.add(g.key)
        mutated = true
      }
    }
    if (mutated) onCollapsedKeysChange(next)
  }, [visibleGroups, collapsedKeys, onCollapsedKeysChange])

  return {
    groups,
    visibleGroups,
    flatItems,
    totalCount: items.length,
    groupCount: groups.length,
    collapsedKeys: expansion.collapsedKeys,
    toggleGroup: expansion.toggleGroup,
    collapseAllGroups,
    expandAllGroups,
    incrementalCollapse,
    incrementalExpand,
    canCollapseMore,
    canExpandMore,
  }
}
